import { roundToNearestHundred } from '../../core/Core';
import type { ExternalPortCostEstimates } from '../../hooks/usePortCostEstimates';
import { PORT_COST_MISSING_PRIMARY_ATTRIBUTE_KEY } from '../../hooks/usePortCostEstimates';
import { getCurrencyRate, type GlobalStore } from '../../../state/zustand';
import type { CostParam } from './costs';

/**
 * Get historical port cost estimates
 * @param locationName
 * @param vesselName
 * @param primaryVesselAttributeId
 * @param byName
 * @param byPrimaryAttributeId
 */

export type HistoricalPortCostEstimates = {
  min: number | undefined;
  avg: number | undefined;
  median: number | undefined;
  max: number | undefined;
  count: number;
  currency: string;
  cost: number;
  match: 'type' | 'name';
  typeAttributeId?: number;
};

export function getHistoricalPortCostEstimates({
  locationName,
  vesselName,
  primaryVesselAttributeId,
  byName,
  byPrimaryAttributeId,
}: {
  locationName: string;
  vesselName: string;
  primaryVesselAttributeId: number | null | undefined;
  byName: ExternalPortCostEstimates;
  byPrimaryAttributeId: ExternalPortCostEstimates;
}): HistoricalPortCostEstimates | undefined {
  locationName = locationName.toUpperCase();
  let costByType = primaryVesselAttributeId
    ? byPrimaryAttributeId.get(
        locationName,
        primaryVesselAttributeId.toString(),
      )
    : undefined;

  if (!costByType) {
    costByType = byPrimaryAttributeId.get(
      locationName,
      PORT_COST_MISSING_PRIMARY_ATTRIBUTE_KEY,
    );
  }

  if (costByType?.median) {
    return {
      ...costByType,
      cost: roundToNearestHundred(costByType.median),
      match: 'type',
      typeAttributeId: primaryVesselAttributeId ?? undefined,
    };
  }
  const costByName = byName.get(locationName, vesselName);
  if (costByName?.median) {
    return {
      ...costByName,
      cost: roundToNearestHundred(costByName.median),
      match: 'name',
    };
  }
  return undefined;
}

export function mapExternalPortCostEstimatesToParams(
  pce: HistoricalPortCostEstimates | undefined,
  currencyRates: GlobalStore['currencyRates'],
) {
  const { min, max, count, match, currency } = { ...pce };
  const params: CostParam[] = [];

  if (min) {
    params.push({
      testId: 'port-cost-min',
      name: 'Min',
      value: min.toFixed(2),
      source: 'SEABER',
    });
  }

  if (max) {
    params.push({
      testId: 'port-cost-max',
      name: 'Max',
      value: max.toFixed(2),
      source: 'SEABER',
    });
  }

  if (count) {
    params.push({
      testId: 'port-cost-similar-calls',
      name: 'Similar calls',
      value: count,
      source: 'SEABER',
    });
  }

  if (match) {
    params.push({
      testId: 'port-cost-match-by-vessel',
      name: 'Match by vessel',
      value: match,
      source: 'SEABER',
    });
  }

  if (currency && currencyRates && currency !== currencyRates?.baseCurrency) {
    params.push({
      testId: 'port-cost-original-currency',
      name: 'Original currency',
      value: currency,
      source: 'SEABER',
    });
    params.push({
      testId: 'port-cost-exchange-rate',
      name: 'Exchange rate',
      value: `1 ${currencyRates?.baseCurrency} = ${getCurrencyRate(
        currency,
      )} ${currency}`,
      source: 'SEABER',
    });
  }

  return params;
}
